import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import styles from './AuthPages.module.css';
import navStyles from '../../components/layouts/PublicLayout.module.css';

/**
 * Pure: the post-sign-in destination, or `null` when the requested one is unsafe.
 *
 * ONLY a same-origin PATH is accepted. `//evil.example` is protocol-relative — the browser
 * reads it as an absolute address on another host — and `https://evil.example` is absolute
 * outright; following either turns the sign-in form into an open redirect. A backslash is
 * rejected too, because browsers normalise `/\` to `//` before resolving. Anything refused
 * falls back to the page's ordinary destination, so a bad link degrades to today's behaviour.
 */
const safeRedirect = (value) => {
  if (typeof value !== 'string' || !value.startsWith('/')) return null;
  if (value.startsWith('//') || value.startsWith('/\\')) return null;
  return value;
};

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const { login, user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Basic validation
    const newErrors = {};
    if (!email) newErrors.email = 'Email is required';
    if (!password) newErrors.password = 'Password is required';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const response = await login(email, password);
      toast.success('Welcome back!');

      // Redirect admin/staff users to admin panel, regular users to dashboard — unless the
      // user was deep-linked here from a guarded route, in which case return them to that
      // route. `safeRedirect` refuses anything that is not a same-origin path, so an
      // attacker-supplied `?redirect=` cannot move the browser off this host.
      const isAdmin = response.user?.role === 'admin' || response.user?.role === 'super_admin' || response.user?.role === 'agency_admin' || response.user?.role === 'staff';
      const fallback = isAdmin ? '/admin' : '/dashboard';
      navigate(safeRedirect(searchParams.get('redirect')) ?? fallback);
    } catch (error) {
      // Card 54619 -- the API now answers a stable machine code next to the prose.
      // The message is ALWAYS shown (it is the whole point: say which problem it is);
      // the code only decides whether we can also offer a way OUT of it. `error.data`
      // is the full parsed body, kept by the client on every non-2xx response.
      const reason = error?.data?.reason ?? null;
      const message = error?.message || 'Invalid email or password';
      toast.error(message);
      setErrors({ general: message, reason });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.loginPage}>
      {/* Floating Navbar - same as landing page */}
      <nav className={`${navStyles.navbar} ${scrolled ? navStyles.navbarScrolled : ''}`}>
        <Link to="/" className={navStyles.logo}>
          <img loading="lazy" src="/oval-logo.webp" alt="OVAL" className={navStyles.logoImg} />
        </Link>
        <div className={navStyles.navActions}>
          {user ? (
            <Link to="/dashboard" className={navStyles.signUpBtn}>Dashboard</Link>
          ) : (
            <>
              <Link to="/login" className={navStyles.signInBtn}>Member Login</Link>
              <Link to="/register" className={navStyles.signUpBtn}>Inquire</Link>
            </>
          )}
        </div>
      </nav>

      {/* Left Side - Hero Image */}
      <div className={styles.heroSide}>
        <img
          src="/images/log-in-page/login-hero.webp"
          alt="Oval Care"
          className={styles.heroImage}
        />
        <div className={styles.heroOverlay}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              Care<br />
              that's Clear,<br />
              Not Loud
            </h1>
            <p className={styles.heroSubtitle}>
              Discover Oval's modern approach to wellness, where frosted clarity meets quiet confidence.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className={styles.formSide}>
        <div className={styles.formContainer}>
          <div className={styles.formCard}>
            <div className={styles.formHeader}>
              <h2 className={styles.formTitle}>Welcome Back!</h2>
              <p className={styles.formSubtitle}>Your trusted digital pharmacy, safe and simple.</p>
            </div>

            <form onSubmit={handleSubmit} className={styles.form}>
              {errors.general && (
                <div className={styles.errorBanner}>{errors.general}</div>
              )}
              {/* Card 54619 -- an account with no password can never succeed by retrying.
                  The reset flow is the only way forward, so it is offered inline and
                  carries the address already typed, so the person does not retype it. */}
              {errors.reason === 'no_credentials' && (
                <Link
                  to={`/forgot-password${email ? `?email=${encodeURIComponent(email)}` : ''}`}
                  className={styles.forgotLink}
                >
                  Set your password
                </Link>
              )}

              <div className={styles.inputGroup}>
                <div className={styles.inputWrapper}>
                  <span className={styles.inputIcon}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                  </span>
                  <input
                    type="email"
                    placeholder="Please enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`${styles.input} ${errors.email ? styles.inputError : ''}`}
                    autoComplete="email"
                  />
                </div>
                {errors.email && <span className={styles.fieldError}>{errors.email}</span>}
              </div>

              <div className={styles.inputGroup}>
                <div className={styles.inputWrapper}>
                  <span className={styles.inputIcon}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                    </svg>
                  </span>
                  <input
                    type="password"
                    placeholder="Please enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={`${styles.input} ${errors.password ? styles.inputError : ''}`}
                    autoComplete="current-password"
                  />
                </div>
                {errors.password && <span className={styles.fieldError}>{errors.password}</span>}
              </div>

              <div className={styles.formOptions}>
                <label className={styles.rememberMe}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span>Remember me</span>
                </label>
                <Link to="/forgot-password" className={styles.forgotLink}>
                  Forgot Password?
                </Link>
              </div>

              <button 
                type="submit" 
                className={styles.loginButton}
                disabled={loading}
              >
                {loading ? 'Signing in...' : 'Login'}
              </button>
            </form>

            <div className={styles.formFooterLink}>
              <span>New to Oval? </span>
              <Link to="/register" className={styles.supportLink}>Contact Support Team</Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default LoginPage;
